import { Button } from '@statsbomb/kitbag-components';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import useApi, { API_ENDPOINTS } from '../../hooks/api';
import { sourceSerialiser } from '../../serialisers/sourceData';
import { videoReferenceSerialiser } from '../../serialisers/videoReference';
import { videoReferencePropTypes } from '../../types/videoReference';

export function Ingest({
  children,
  filename,
  filenamePrefix,
  onError,
  onSent,
  onStart,
  qualitySettings,
  sourceData,
  urls,
  variant,
  videoReference,
}) {
  const api = useApi();
  const intl = useIntl();

  /**
   * Validates the props and sends error messages to onError handlers if they are missing.
   * Won't fire the API call if the onStart call back returns false, so could be used for validation.
   *
   * @returns {Promise<*>|Function}
   */
  const handleClick = () => {
    if (!filename) {
      return onError(
        intl.formatMessage({
          defaultMessage: 'Filename not provided',
          description: 'Ingest button validation error: filename',
          id: '1437e',
        }),
      );
    }

    if (urls.length === 0) {
      return onError(
        intl.formatMessage({
          defaultMessage: 'No URLs to ingest',
          description: 'Ingest button validation error: urls',
          id: '04a41',
        }),
      );
    }

    /**
     * Send the data to the API.
     * @returns {Boolean|*}
     */
    const send = async () => {
      const data = JSON.stringify({
        name: `${filenamePrefix}_${filename}`,
        quality_settings: qualitySettings,
        segments: urls,
        ...sourceSerialiser(sourceData),
        video_reference: videoReferenceSerialiser(videoReference),
      });

      const result = await api.put(
        `${API_ENDPOINTS.videoService}/videos/ingest`,
        { data },
      );

      return result
        ? onSent()
        : onError(
            intl.formatMessage({
              defaultMessage: 'There has been an error ingesting this file',
              id: '9a3f1',
            }),
          );
    };
    return onStart() && send();
  };

  return (
    <Button isPending={api.isFetching} onClick={handleClick} variant={variant}>
      {children || (
        <FormattedMessage defaultMessage="Ingest & Convert" id="31eb0" />
      )}
    </Button>
  );
}

Ingest.propTypes = {
  children: PropTypes.node,
  filename: PropTypes.string,
  filenamePrefix: PropTypes.string,
  onError: PropTypes.func,
  onSent: PropTypes.func,
  // Optional function if defined is fired upon start
  // also used to validate as if it returns false then error is bubbled up.
  onStart: PropTypes.func,
  qualitySettings: PropTypes.shape({
    bitrate: PropTypes.string,
    framerate: PropTypes.string,
    resolution: PropTypes.shape({
      height: PropTypes.string,
      width: PropTypes.string,
    }),
  }).isRequired,
  sourceData: PropTypes.shape({
    isLicensed: PropTypes.string,
    sourceName: PropTypes.string,
    sourceType: PropTypes.string,
    uploadMethod: PropTypes.string,
  }).isRequired,
  urls: PropTypes.arrayOf(PropTypes.string),
  variant: PropTypes.string,
  videoReference: PropTypes.shape(videoReferencePropTypes).isRequired,
};

Ingest.defaultProps = {
  children: undefined,
  filename: undefined,
  filenamePrefix: 'ingested',
  onError: () => {},
  onSent: () => {},
  onStart: () => true,
  urls: [],
  variant: 'primary',
};

export default { Ingest };
